import axios from 'axios';
import { ProdEnv } from '../config/env';
import { isOnline, showNoInternetPopup } from '../utils/networkStatus';

const apiClient = axios.create({
  baseURL: ProdEnv?.BASE_URL,
  withCredentials: true, 
  headers: {
    'Content-Type': 'application/json',
  }
});

const rawAxios = axios.create({
  baseURL: ProdEnv?.BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});

const REFRESH_ENDPOINT = '/Auth/refreshToken';

const callRefreshTokenApi = async () => {
  const response = await rawAxios.post(REFRESH_ENDPOINT, {});
  return response.data;
};

// **REQUEST INTERCEPTOR**
apiClient.interceptors.request.use(
  (config) => {
    if (!isOnline()) {
      showNoInternetPopup();
      return Promise.reject(new axios.Cancel('No internet connection.'));
    }

    // **TOKEN HANDLING**
    const authTokenString = localStorage.getItem('authToken');

    if (authTokenString && !config.url.includes('/Auth/login')) {
      try {
        let tokenValue;
        const parsedToken = JSON.parse(authTokenString);

        if (parsedToken.token || parsedToken.accessToken || parsedToken.access_token) {
          tokenValue = parsedToken.token || parsedToken.accessToken || parsedToken.access_token;
        } else if (typeof parsedToken === 'string') {
          tokenValue = parsedToken;
        }

        if (tokenValue) {
          config.headers['Authorization'] = `Bearer ${tokenValue}`;
        }
      } catch (error) {
        if (authTokenString.startsWith('eyJ')) {
          config.headers['Authorization'] = `Bearer ${authTokenString}`;
        } else {
          localStorage.removeItem('authToken');
          localStorage.removeItem('userData');
        }
      }
    }

    const userDataString = localStorage.getItem('userData');
    if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        if (userData.userId) {
          config.headers['sentryUserId'] = userData.userId.toString();
        }
        if (userData.email) {
          config.headers['name'] = userData.email;
        }
      } catch (error) {
        console.error('Error parsing userData:', error);
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (!error.response) {
      console.error('Network error or server unreachable');
      showNoInternetPopup();
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;
      try {
        const res = await callRefreshTokenApi();

        localStorage.setItem('authToken', res.accessToken);
        if (res.expiresIn) {
          localStorage.setItem('expiresIn', res.expiresIn);
        }
        processQueue(null, res.accessToken);
        isRefreshing = false;

        originalRequest.headers['Authorization'] = `Bearer ${res.accessToken}`;
        return apiClient(originalRequest);

      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;

        localStorage.clear();
        sessionStorage.clear();
        if (window.location.pathname !== '/') {
          window.location.href = '/';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;